import { useQuery } from '@tanstack/react-query';
import type { Ledger } from '@fpc/shared';
import { api } from '@/lib/api';

/**
 * The company's active expense and asset ledgers — what was bought. Suggested
 * on a vendor and at review, decided by accounting at verification.
 */
export function useExpenseLedgers(companyId: string | undefined, enabled = true) {
  const query = useQuery({
    queryKey: ['settings-ledgers', companyId, 'general'],
    queryFn: () => api.settings.ledgers({ companyId: companyId!, general: 'true', pageSize: 200 }),
    enabled: !!companyId && enabled,
  });
  const ledgers = (query.data?.items ?? []).filter(
    (ledger) => ledger.active && (ledger.type === 'EXPENSE' || ledger.type === 'ASSET'),
  );
  return { ledgers, loaded: query.isSuccess };
}

/** The `<option>`s of an expense ledger `<select>`, under their group headings. */
export function ExpenseLedgerOptions({ ledgers }: { ledgers: Ledger[] }) {
  return (
    <>
      {groupedLedgers(ledgers).map(([group, items]) => (
        <optgroup key={group} label={group}>
          {items.map((ledger) => (
            <option key={ledger.id} value={ledger.id}>
              {ledger.code} · {ledger.name}
            </option>
          ))}
        </optgroup>
      ))}
    </>
  );
}

/** Ledgers under their group headings, groups in order, ungrouped last. */
function groupedLedgers(ledgers: Ledger[]): Array<[string, Ledger[]]> {
  const groups = new Map<string, Ledger[]>();
  for (const ledger of ledgers) {
    const group = ledger.group || 'Other';
    groups.set(group, [...(groups.get(group) ?? []), ledger]);
  }
  return [...groups.entries()].sort(([a], [b]) =>
    a === 'Other' ? 1 : b === 'Other' ? -1 : a.localeCompare(b),
  );
}
